/**
 * AİLE BAŞINA KESİN İFADE SÖZLÜĞÜ (CLOS-1086/1087/1088/1089/1090 `[S05]`/`[S06]`).
 *
 * ### ⚠ NEDEN AİLE BAŞINA AYRI LİSTE
 *
 * Beş kalite kartı aynı şeyi farklı kelimelerle istiyor: *"kesin suçlama dili yok"*
 * (hesap güvenliği), *"privilege-escalation"* (organizasyon), *"false-match"*
 * (müvekkil), *"no-auto-action"* (Ana Sayfa), *"no-financial-action"* (kullanım).
 *
 * Tek bir liste yazmak cazipti ve yanlış olurdu: risk ailesinin yasağı ("geçersiz",
 * "hukuka aykırı") güvenlik sinyalinde ZARARSIZ bir kelime; güvenliğin yasağı
 * ("saldırı tespit edildi") sözleşme analizinde hiç geçmez. Ortak bir liste ya çok geniş
 * olup her çıktıyı işaretler — o zaman uyarı hiçbir şey anlatmaz — ya da çok dar olup
 * hiçbir aileyi doğru korur.
 *
 * ### ⚠ İŞARETLENİYOR, REDDEDİLMİYOR
 *
 * `RISK_CERTAINTY_PATTERNS`'ın kararı burada da geçerli: çıktı reddedilse kullanıcının
 * kredisi bir kelime seçimi yüzünden yanardı; cümle sessizce düzeltilse AI'ın ne dediği
 * kullanıcıdan gizlenirdi. Ekran uyarıyı bulgunun yanına koyuyor.
 *
 * ### ⚠ LİSTELER DAR VE KÖK BİÇİMLERDEN
 *
 * Türkçe çekim ekleri (-dır, -dir, -tır) sonradan geldiği için önek eşleşmesi yeterli.
 * ⚠ Kalıplar KÜÇÜK HARFLE ve `toLocaleLowerCase("tr")` ile karşılaştırılıyor: noktalı İ
 * tuzağı yüzünden (JavaScript `i` bayrağı `İ` ile `i`'yi eşleştirmiyor) büyük harfli
 * girdi başka türlü kaçardı.
 */

export const AI_CERTAINTY_FAMILIES = [
  "account_security",
  "organization",
  "client",
  "workday",
  "ops",
] as const;
export type AiCertaintyFamily = (typeof AI_CERTAINTY_FAMILIES)[number];

export const AI_CERTAINTY_PATTERNS: Record<AiCertaintyFamily, readonly string[]> = {
  /**
   * ⚠ Hesap güvenliği: SUÇLAMA dili. Kart: *"kullanıcıyı suç/fraud/ihlalle kesin biçimde
   * etiketlemez."* Bir sinyal örüntü bildirmeli, hüküm vermemeli.
   */
  account_security: [
    "saldırı tespit",
    "hesap ele geçiril",
    "yetkisiz erişim var",
    "kesinlikle saldır",
    "dolandırıcılık",
    "sahtekârlık",
    "hesabınız hacklen",
  ],
  /**
   * ⚠ Organizasyon: YETKİ YÜKSELTME emri. Kart: *"privilege-escalation"* — öneri bir
   * yetkinin verilmesi GEREKTİĞİNİ kesin dille söylememeli; kararı insan verir.
   */
  organization: [
    "derhal yetki ver",
    "mutlaka admin",
    "yönetici yapılmalı",
    "kesinlikle yetkilendir",
    "güvenlik açığı var",
  ],
  /**
   * ⚠ Müvekkil: KESİN EŞLEŞME iddiası. Kart: *"false-match"* — iki kaydın aynı kişi/şirket
   * olduğu ancak insan doğrulamasıyla söylenebilir.
   */
  client: ["aynı kişidir", "aynı şirkettir", "kesinlikle aynı", "mükerrer kayıttır", "birleştirin"],
  /**
   * ⚠ Ana Sayfa: OTOMATİK EYLEM dili. Kart: *"no-auto-action"* — özet bir işin yapıldığını
   * ya da yapılacağını söylememeli; yalnız dikkat çekmeli.
   */
  workday: [
    "sizin adınıza yapıldı",
    "otomatik olarak onayland",
    "sistem gönderdi",
    "tamamlandı bile",
  ],
  /**
   * ⚠ Kullanım/abonelik: TİCARİ İŞLEM dili. Kart: *"no-financial-action"* — içgörü bir
   * satın alma, yükseltme ya da ödeme yapıldığını/yapılacağını ima etmemeli.
   */
  ops: [
    "planınız yükseltild",
    "satın alındı",
    "ödeme alındı",
    "otomatik olarak yükseltilecek",
    "faturanız kesildi",
  ],
};

/** ⚠ Aile bilinmiyorsa DENETLENMİYOR değil, HATA: sessiz geçiş kapının amacını bozardı. */
export function aiKesinlikIddiaEdiyor(aile: AiCertaintyFamily, metin: string): boolean {
  const kalipar = AI_CERTAINTY_PATTERNS[aile];
  const kucuk = metin.toLocaleLowerCase("tr");
  return kalipar.some((kalip) => kucuk.includes(kalip));
}

export const AI_CERTAINTY_WARNING =
  "AI bu satırda kesin bir ifade kullandı; bu bir karar ya da yapılmış bir işlem değildir.";
